import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, updateDoc, onSnapshot, addDoc, collection, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { buyPlumes } from '../utils/stripe';
import './Plumes.css';

const PLUME_PACKS = [
  { id: 'pack_50',   amount: 50,   price: '0,99€', emoji: '🪶',    label: 'Petit nid',    stripeId: 'price_plumes_50' },
  { id: 'pack_200',  amount: 200,  price: '4,49€', emoji: '🪶🪶',   label: 'Belle plume',  stripeId: 'price_plumes_200' },
  { id: 'pack_500',  amount: 500,  price: '10,99€', emoji: '🪶🪶🪶',  label: "Vol d'oiseau", stripeId: 'price_plumes_500' },
  { id: 'pack_1500', amount: 1500, price: '32,99€', emoji: '✨🪶✨', label: 'Aile dorée',   stripeId: 'price_plumes_1500' },
];

const TIP_OPTIONS = [
  { amount: 10, emoji: '🪶', label: 'Un merci' },
  { amount: 50, emoji: '☕', label: 'Un café' },
  { amount: 100, emoji: '📖', label: 'Un livre' },
  { amount: 500, emoji: '🎁', label: 'Un cadeau' },
];

const BOOST_OPTIONS = [
  { amount: 100, duration: '24h', emoji: '⚡', label: 'Boost Express' },
  { amount: 300, duration: '3 jours', emoji: '🚀', label: 'Boost Pro' },
  { amount: 700, duration: '7 jours', emoji: '💎', label: 'Boost Premium' },
];

export function usePlumes() {
  const { user } = useUser();
  const [plumes, setPlumes] = useState(0);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'userLibraries', user.id), (snapshot) => {
      if (snapshot.exists()) {
        setPlumes(snapshot.data().plumes || 0);
      }
    });
    return () => unsub();
  }, [user]);

  const addPlumes = async (amount, reason) => {
    if (!user) return;
    const newTotal = plumes + amount;
    const ref = doc(db, 'userLibraries', user.id);
    await updateDoc(ref, { plumes: newTotal });

    // Logger la transaction
    await addDoc(collection(db, 'plumeTransactions'), {
      userId: user.id,
      amount,
      type: amount > 0 ? 'earn' : 'spend',
      reason: reason || 'unknown',
      balance: newTotal,
      createdAt: serverTimestamp(),
    });
  };

  const spendPlumes = async (amount, reason) => {
    if (!user || plumes < amount) return false;
    const newTotal = plumes - amount;
    const ref = doc(db, 'userLibraries', user.id);
    await updateDoc(ref, { plumes: newTotal });

    await addDoc(collection(db, 'plumeTransactions'), {
      userId: user.id,
      amount: -amount,
      type: 'spend',
      reason: reason || 'unknown',
      balance: newTotal,
      createdAt: serverTimestamp(),
    });
    return true;
  };

  const sendPlumesToAuthor = async (authorId, authorName, amount) => {
    if (!user || plumes < amount) return false;
    const success = await spendPlumes(amount, `Envoyé à ${authorName}`);
    if (!success) return false;

    // Ajouter les plumes à l'auteur
    try {
      const authorRef = doc(db, 'userLibraries', authorId);
      const authorSnap = await getDocs(query(collection(db, 'userLibraries'), where('__name__', '==', authorId)));
      // On utilise un addDoc pour notifier
      await addDoc(collection(db, 'plumeTransactions'), {
        userId: authorId,
        amount,
        type: 'receive',
        reason: `Reçu de ${user.name}`,
        createdAt: serverTimestamp(),
      });
      // Incrémenter les plumes de l'auteur
      await updateDoc(authorRef, { plumes: (authorSnap.docs[0]?.data()?.plumes || 0) + amount });
          // Notification à l'auteur
      const { sendNotification } = await import('../utils/notifications');
      sendNotification({
        recipientId: authorId,
        fromId: user.id,
        fromName: user.name,
        type: 'plumes',
      });
    
    } catch (e) {
      console.error('Erreur envoi plumes auteur:', e);
    }
    return true;
  };

  return { plumes, addPlumes, spendPlumes, sendPlumesToAuthor };
}

export default function Plumes() {
  const { plumes } = usePlumes();
  const [showShop, setShowShop] = useState(false);

  return (
    <>
      <motion.div
        className="plumes-cta-card"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="plumes-cta-top">
          <div className="plumes-cta-balance">
            <span className="plumes-icon">🪶</span>
            <span className="plumes-count">{plumes.toLocaleString('fr-FR')}</span>
            <span className="plumes-label">plumes</span>
          </div>
          <motion.button
            className="plumes-cta-buy-btn"
            onClick={() => setShowShop(!showShop)}
            whileTap={{ scale: 0.95 }}
          >
            {showShop ? 'Fermer' : '+ Acheter des plumes'}
          </motion.button>
        </div>

        <p className="plumes-cta-desc">Soutiens tes auteurs préférés et booste les livres que tu aimes !</p>

        <AnimatePresence>
          {showShop && (
            <motion.div
              className="plumes-shop-inline"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div className="plumes-packs">
                {PLUME_PACKS.map(pack => (
                  <motion.button
                    key={pack.id}
                    className="plumes-pack"
                    whileTap={{ scale: 0.95 }}
                    whileHover={{ y: -2 }}
                    onClick={() => buyPlumes(pack.stripeId)}
                  >
                    <span className="plumes-pack-emoji">{pack.emoji}</span>
                    <strong className="plumes-pack-amount">{pack.amount} 🪶</strong>
                    <span className="plumes-pack-label">{pack.label}</span>
                    <span className="plumes-pack-price">{pack.price}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
}

export function PlumesInfo() {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const { user } = useUser();

  const loadHistory = async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, 'plumeTransactions'),
        where('userId', '==', user.id),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Erreur chargement historique:', e);
    }
  };

  return (
    <div className="plumes-info">
      <h3 className="plumes-info-title">💸 Que faire avec des plumes ?</h3>
      <div className="plumes-info-list">
        <div className="plumes-info-item"><span>Soutenir un auteur</span><span>10 à 500 🪶</span></div>
        <div className="plumes-info-item"><span>Booster un livre (24h)</span><span>100 🪶</span></div>
        <div className="plumes-info-item"><span>Booster un livre (3 jours)</span><span>300 🪶</span></div>
        <div className="plumes-info-item"><span>Booster un livre (7 jours)</span><span>700 🪶</span></div>
      </div>

      <button className="plumes-history-btn" onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}>
        {showHistory ? <><IoChevronUp /> Masquer l'historique</> : <><IoChevronDown /> Voir l'historique</>}
      </button>

      <AnimatePresence>
        {showHistory && (
          <motion.div
            className="plumes-history"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            {history.length > 0 ? history.slice(0, 20).map(tx => (
              <div key={tx.id} className={'plumes-history-item ' + (tx.amount > 0 ? 'plumes-history-earn' : 'plumes-history-spend')}>
                <span className="plumes-history-amount">{tx.amount > 0 ? '+' : ''}{tx.amount} 🪶</span>
                <span className="plumes-history-reason">{tx.reason}</span>
                <span className="plumes-history-date">{tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleDateString('fr-FR') : ''}</span>
              </div>
            )) : (
              <p className="plumes-history-empty">Aucune transaction pour le moment</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}